/**
 * Wine Tasting page - scroll reveal.
 * Mirrors the Club page reveal contract with Wine Tasting-specific selectors.
 *
 * - IntersectionObserver owns viewport entry.
 * - GSAP owns motion only.
 * - Reduced motion, missing GSAP, or missing IntersectionObserver renders static.
 * - Theme editor section reloads can safely re-bind unrevealed elements.
 */
;(function () {
	window.__lTastingRevealActive = true

	var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
	var revealObserver = null

	function initTastingReveal() {
		var els = document.querySelectorAll('[data-l-tasting-reveal]:not(.is-revealed)')

		if (!els.length) {
			document.documentElement.classList.remove('l-tasting-reveal-prep')

			return
		}

		if (prefersReducedMotion || !window.gsap || !window.IntersectionObserver) {
			revealTastingStatic()

			return
		}

		if (revealObserver) revealObserver.disconnect()

		revealObserver = new IntersectionObserver(handleRevealEntries, {
			root: null,
			rootMargin: '0px 0px -12% 0px',
			threshold: 0.01,
		})

		els.forEach(function (el) {
			el.classList.add('is-reveal-bound')
			revealObserver.observe(el)
		})
	}

	function handleRevealEntries(entries) {
		entries.forEach(function (entry) {
			if (!entry.isIntersecting) return

			revealObserver.unobserve(entry.target)
			try {
				playSectionReveal(entry.target)
			} catch (e) {
				markRevealComplete(entry.target)
			}
		})
	}

	function playSectionReveal(el) {
		if (el.classList.contains('is-revealed') || el.classList.contains('is-revealing')) return

		var delay = parseFloat(el.dataset.lTastingRevealDelay) || 0
		var config = getRevealConfig(el)
		var parts = getRevealParts(el)
		var media = getRevealMedia(el)
		var animatedTargets = [el].concat(parts, media)
		var tl = window.gsap.timeline({
			delay: delay,
			defaults: {
				ease: 'power3.out',
				overwrite: 'auto',
			},
			onComplete: function () {
				markRevealComplete(el)
				window.gsap.set(animatedTargets.length ? animatedTargets : el, {
					clearProps: 'opacity,visibility,transform,willChange',
				})
			},
		})

		el.classList.add('is-revealing')

		window.gsap.set(animatedTargets.length ? animatedTargets : el, {
			willChange: 'transform,opacity',
		})

		tl.fromTo(el, getRevealFrom(config.shell), getRevealTo(config.shell), 0)

		if (parts.length) {
			tl.fromTo(parts, getRevealFrom(config.parts), getRevealTo(config.parts), 0.08)
		}

		if (media.length) {
			tl.fromTo(media, getRevealFrom(config.media), getRevealTo(config.media), config.mediaAt)
		}
	}

	function markRevealComplete(el) {
		el.classList.remove('is-reveal-bound')
		el.classList.remove('is-revealing')
		el.classList.add('is-revealed')

		if (!document.querySelector('[data-l-tasting-reveal]:not(.is-revealed)')) {
			document.documentElement.classList.remove('l-tasting-reveal-prep')
		}
	}

	function revealTastingStatic() {
		document.documentElement.classList.remove('l-tasting-reveal-prep')

		document.querySelectorAll('[data-l-tasting-reveal]').forEach(function (el) {
			markRevealComplete(el)
		})
	}

	function getRevealFrom(vars) {
		var from = {}

		copyRevealProp(vars, from, 'x')
		copyRevealProp(vars, from, 'y')
		copyRevealProp(vars, from, 'scale')
		copyRevealProp(vars, from, 'transformOrigin')

		if (vars.opacity !== undefined) from.autoAlpha = vars.opacity

		return from
	}

	function getRevealTo(vars) {
		var to = {}

		copyRevealProp(vars, to, 'duration')
		copyRevealProp(vars, to, 'ease')
		copyRevealProp(vars, to, 'stagger')
		copyRevealProp(vars, to, 'transformOrigin')

		if (vars.opacity !== undefined) to.autoAlpha = 1
		if (vars.x !== undefined) to.x = 0
		if (vars.y !== undefined) to.y = 0
		if (vars.scale !== undefined) to.scale = 1

		return to
	}

	function copyRevealProp(source, target, prop) {
		if (source[prop] !== undefined) {
			target[prop] = source[prop]
		}
	}

	function getRevealConfig(el) {
		var config = {
			shell: {
				opacity: 0,
				y: 18,
				duration: 0.68,
				ease: 'power2.out',
			},
			parts: {
				opacity: 0,
				y: 16,
				scale: 0.99,
				duration: 0.72,
				stagger: { each: 0.055, from: 'start' },
			},
			media: {
				scale: 1.025,
				duration: 1,
				ease: 'power2.out',
				transformOrigin: '50% 55%',
			},
			mediaAt: 0.08,
		}

		if (el.classList.contains('l-tasting-hero__inner')) {
			config.shell = {
				opacity: 0,
				scale: 0.985,
				duration: 0.78,
				ease: 'power2.out',
			}
			config.parts = {
				opacity: 0,
				y: -10,
				scale: 0.985,
				duration: 0.85,
				stagger: 0.06,
				transformOrigin: '50% 50%',
			}
			config.media.scale = 1.035
			config.media.duration = 1.2
			config.mediaAt = 0
		} else if (el.classList.contains('l-tasting-exp__item')) {
			config.parts = {
				opacity: 0,
				x: function (index) {
					return index === 0 ? -12 : 12
				},
				y: 0,
				scale: 0.99,
				duration: 0.76,
				stagger: { each: 0.06, from: 'start' },
			}
			config.media.scale = 1.025
			config.mediaAt = 0.1
		} else if (el.classList.contains('l-tasting-visiting__inner')) {
			config.parts = {
				opacity: 0,
				y: function (index) {
					return index === 0 ? 10 : 0
				},
				scale: function (index) {
					return index === 0 ? 0.99 : 0.985
				},
				duration: 0.72,
				stagger: { each: 0.055, from: 'start' },
			}
			config.media.scale = 1.02
			config.mediaAt = 0.14
		}

		return config
	}

	function getRevealParts(el) {
		var selector

		if (el.classList.contains('l-tasting-hero__inner')) {
			selector = '.l-tasting-hero__title'
		} else if (el.classList.contains('l-tasting-intro__inner')) {
			selector = '.l-tasting-heading, .l-tasting-intro__body, .l-tasting-cta'
		} else if (el.classList.contains('l-tasting-exp__item')) {
			selector = '.l-tasting-exp__media, .l-tasting-exp__card'
		} else if (el.classList.contains('l-tasting-visiting__inner')) {
			selector =
				'.l-tasting-visiting__heading, .l-tasting-visiting__ducks, .l-tasting-visiting__card, .l-tasting-visiting__image'
		} else if (el.classList.contains('l-tasting-tips__inner')) {
			selector = '.l-tasting-heading, .l-tasting-tips__content'
		}

		return selector ? getVisibleNodes(el.querySelectorAll(selector)) : []
	}

	function getRevealMedia(el) {
		return getVisibleNodes(
			el.querySelectorAll(
				'.l-tasting-hero__media img, .l-tasting-hero__media svg, .l-tasting-hero__media video, .l-tasting-exp__media img, .l-tasting-exp__media svg, .l-tasting-exp__media video, .l-tasting-visiting__image img, .l-tasting-visiting__image svg'
			)
		)
	}

	function getVisibleNodes(nodes) {
		var seen = []

		return Array.prototype.filter.call(nodes, function (node) {
			if (!node || seen.indexOf(node) !== -1 || !node.getClientRects().length) return false
			seen.push(node)

			return true
		})
	}

	try {
		initTastingReveal()
	} catch (e) {
		revealTastingStatic()
	}

	if (window.Shopify && window.Shopify.designMode) {
		document.addEventListener('shopify:section:load', function () {
			document.documentElement.classList.remove('l-tasting-reveal-prep')
			try {
				initTastingReveal()
			} catch (e) {
				revealTastingStatic()
			}
		})
	}
})()

/**
 * Wine Tasting experience media slider.
 * Uses native scroll snap so it stays light and works with theme editor reloads.
 */
;(function () {
	if (!window.customElements || window.customElements.get('l-tasting-experience-slider')) return

	var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

	class LTastingExperienceSlider extends HTMLElement {
		connectedCallback() {
			this.viewport = this.querySelector('[data-tasting-slider-viewport]')
			this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-tasting-slider-slide]'))
			this.prevButton = this.querySelector('[data-tasting-slider-prev]')
			this.nextButton = this.querySelector('[data-tasting-slider-next]')
			this.loop = this.dataset.loop === 'true'
			this.autoplay = this.dataset.autoplay === 'true'
			this.interval = Math.max(parseInt(this.dataset.interval, 10) || 5000, 1000)
			this.frame = null
			this.timer = null
			this.resizeObserver = null
			this.isDragging = false
			this.dragMoved = false
			this.dragResetTimer = null
			this.dragStartX = 0
			this.dragStartScrollLeft = 0

			this.boundPrev = this.goPrevious.bind(this)
			this.boundNext = this.goNext.bind(this)
			this.boundScroll = this.requestUpdate.bind(this)
			this.boundPause = this.pauseAutoplay.bind(this)
			this.boundResume = this.resumeAutoplay.bind(this)
			this.boundPointerDown = this.handlePointerDown.bind(this)
			this.boundPointerMove = this.handlePointerMove.bind(this)
			this.boundPointerUp = this.handlePointerUp.bind(this)
			this.boundClick = this.handleClick.bind(this)

			this.bindEvents()
			this.updateControls()
			this.startAutoplay()
		}

		disconnectedCallback() {
			this.unbindEvents()
			this.stopAutoplay()

			if (this.resizeObserver) {
				this.resizeObserver.disconnect()
				this.resizeObserver = null
			}

			if (this.frame) {
				window.cancelAnimationFrame(this.frame)
				this.frame = null
			}

			if (this.dragResetTimer) {
				window.clearTimeout(this.dragResetTimer)
				this.dragResetTimer = null
			}
		}

		bindEvents() {
			if (!this.viewport || this.slides.length < 2) return

			if (this.prevButton) this.prevButton.addEventListener('click', this.boundPrev)
			if (this.nextButton) this.nextButton.addEventListener('click', this.boundNext)

			this.viewport.addEventListener('scroll', this.boundScroll, { passive: true })
			this.viewport.addEventListener('pointerdown', this.boundPointerDown)
			this.viewport.addEventListener('click', this.boundClick, true)
			this.addEventListener('pointerenter', this.boundPause)
			this.addEventListener('pointerleave', this.boundResume)
			this.addEventListener('focusin', this.boundPause)
			this.addEventListener('focusout', this.boundResume)

			if (window.ResizeObserver) {
				this.resizeObserver = new ResizeObserver(this.boundScroll)
				this.resizeObserver.observe(this.viewport)
			} else {
				window.addEventListener('resize', this.boundScroll)
			}
		}

		unbindEvents() {
			if (this.prevButton) this.prevButton.removeEventListener('click', this.boundPrev)
			if (this.nextButton) this.nextButton.removeEventListener('click', this.boundNext)
			if (this.viewport) this.viewport.removeEventListener('scroll', this.boundScroll)
			if (this.viewport) this.viewport.removeEventListener('pointerdown', this.boundPointerDown)
			if (this.viewport) this.viewport.removeEventListener('click', this.boundClick, true)

			this.removeEventListener('pointerenter', this.boundPause)
			this.removeEventListener('pointerleave', this.boundResume)
			this.removeEventListener('focusin', this.boundPause)
			this.removeEventListener('focusout', this.boundResume)
			window.removeEventListener('pointermove', this.boundPointerMove)
			window.removeEventListener('pointerup', this.boundPointerUp)
			window.removeEventListener('pointercancel', this.boundPointerUp)
			window.removeEventListener('resize', this.boundScroll)
		}

		handlePointerDown(event) {
			if (!this.viewport || this.slides.length < 2 || event.pointerType === 'touch' || event.button !== 0) return

			this.isDragging = true
			this.dragMoved = false
			this.dragStartX = event.clientX
			this.dragStartScrollLeft = this.viewport.scrollLeft
			this.viewport.classList.add('is-dragging')
			this.pauseAutoplay()

			try {
				this.viewport.setPointerCapture(event.pointerId)
			} catch (e) {
				// Pointer capture can fail in older browsers; window listeners below still keep drag usable.
			}

			window.addEventListener('pointermove', this.boundPointerMove, { passive: false })
			window.addEventListener('pointerup', this.boundPointerUp)
			window.addEventListener('pointercancel', this.boundPointerUp)
		}

		handlePointerMove(event) {
			if (!this.isDragging || !this.viewport) return

			var delta = event.clientX - this.dragStartX

			if (Math.abs(delta) > 3) {
				this.dragMoved = true
			}

			this.viewport.scrollLeft = this.dragStartScrollLeft - delta
			event.preventDefault()
		}

		handlePointerUp(event) {
			if (!this.isDragging || !this.viewport) return

			this.isDragging = false
			this.viewport.classList.remove('is-dragging')

			try {
				this.viewport.releasePointerCapture(event.pointerId)
			} catch (e) {
				// Pointer capture may not have been established.
			}

			window.removeEventListener('pointermove', this.boundPointerMove)
			window.removeEventListener('pointerup', this.boundPointerUp)
			window.removeEventListener('pointercancel', this.boundPointerUp)
			this.goTo(this.getIndex())
			this.resumeAutoplay()

			if (this.dragMoved) {
				this.dragResetTimer = window.setTimeout(
					function () {
						this.dragMoved = false
						this.dragResetTimer = null
					}.bind(this),
					160
				)
			}
		}

		handleClick(event) {
			if (!this.dragMoved) return

			event.preventDefault()
			event.stopPropagation()
			this.dragMoved = false
		}

		goPrevious() {
			this.go(-1)
		}

		goNext() {
			this.go(1)
		}

		go(delta) {
			if (!this.viewport || this.slides.length < 2) return

			var index = this.getIndex() + delta
			var max = this.slides.length - 1

			if (this.loop) {
				index = (index + this.slides.length) % this.slides.length
			} else {
				index = Math.max(0, Math.min(index, max))
			}

			this.goTo(index)
			this.restartAutoplay()
		}

		goTo(index) {
			var left = index * this.viewport.clientWidth
			var behavior = prefersReducedMotion.matches ? 'auto' : 'smooth'

			this.viewport.scrollTo({
				left: left,
				behavior: behavior,
			})
			this.updateControls(index)
		}

		requestUpdate() {
			if (this.frame) return

			this.frame = window.requestAnimationFrame(
				function () {
					this.frame = null
					this.updateControls()
				}.bind(this)
			)
		}

		getIndex() {
			if (!this.viewport || !this.viewport.clientWidth) return 0

			return Math.round(this.viewport.scrollLeft / this.viewport.clientWidth)
		}

		updateControls(index) {
			var currentIndex = typeof index === 'number' ? index : this.getIndex()
			var max = this.slides.length - 1

			if (!this.prevButton || !this.nextButton || this.slides.length < 2) return

			this.prevButton.disabled = !this.loop && currentIndex <= 0
			this.nextButton.disabled = !this.loop && currentIndex >= max
		}

		startAutoplay() {
			if (!this.autoplay || this.slides.length < 2 || prefersReducedMotion.matches) return

			this.stopAutoplay()
			this.timer = window.setInterval(
				function () {
					this.go(1)
				}.bind(this),
				this.interval
			)
		}

		stopAutoplay() {
			if (!this.timer) return

			window.clearInterval(this.timer)
			this.timer = null
		}

		restartAutoplay() {
			if (!this.autoplay) return

			this.startAutoplay()
		}

		pauseAutoplay() {
			this.stopAutoplay()
		}

		resumeAutoplay() {
			this.startAutoplay()
		}
	}

	window.customElements.define('l-tasting-experience-slider', LTastingExperienceSlider)
})()
